import { action, observable } from 'mobx';
import { transformJsonData } from '~/utils/data.utils';
import { SocketAuth } from '~/services/adapters/web-sockets.adapter';
import {
    buildTopic,
    WebsocketNamespace,
    getNamespaceParts
} from '~/utils/websocket.util';
import { jwtDecode, InvalidTokenError } from 'jwt-decode';
import { LoggerService } from '~/services/logger/logger.service';
import { IWebSocketsAdapter } from '~/services/interfaces/IWebSocketsAdapter';

class WebsocketTokensStore {
    webSocketsAdapter: IWebSocketsAdapter;

    @observable tokens: Record<string, Promise<string>> = {};

    constructor(webSocketsAdapter: IWebSocketsAdapter) {
        this.webSocketsAdapter = webSocketsAdapter;
    }

    getNamespaceKeyInLocalStorage({
        namespaceName,
        namespaceId
    }: {
        namespaceName: string;
        namespaceId: string;
    }) {
        return `HONEYBOOK_REACT_WEBSOCKET_AUTH.${namespaceName}.${namespaceId}`;
    }

    getNamespaceTokenKey(namespaceName: string, namespaceId: string) {
        return `${namespaceName}.${namespaceId}`;
    }

    @action.bound
    async getToken(namespace: WebsocketNamespace, intent?: string) {
        const { namespaceName, namespaceId } = getNamespaceParts(namespace);
        const namespaceTopicPrefix = this.getNamespaceTokenKey(
            namespaceName,
            namespaceId
        );
        if (this.tokens[namespaceTopicPrefix]) {
            return this.tokens[namespaceTopicPrefix];
        }

        const namespaceTokenInLocalStorageKey =
            this.getNamespaceKeyInLocalStorage({
                namespaceName,
                namespaceId
            });

        const tokenInLocalStorage = localStorage.getItem(
            namespaceTokenInLocalStorageKey
        );

        if (tokenInLocalStorage) {
            this.tokens[namespaceTopicPrefix] =
                Promise.resolve(tokenInLocalStorage);

            return tokenInLocalStorage;
        }

        const tokenPromise = this.webSocketsAdapter.getNamespaceToken(
            namespace,
            intent
        );
        this.tokens[namespaceTopicPrefix] = tokenPromise;
        const token = await tokenPromise;

        localStorage.setItem(namespaceTokenInLocalStorageKey, token);

        return token;
    }

    cleanNamespaceToken(namespace: WebsocketNamespace) {
        const { namespaceName, namespaceId } = getNamespaceParts(namespace);

        const namespaceTopicPrefix = this.getNamespaceTokenKey(
            namespaceName,
            namespaceId
        );
        delete this.tokens[namespaceTopicPrefix];

        const namespaceTokenInLocalStorageKey =
            this.getNamespaceKeyInLocalStorage({
                namespaceName,
                namespaceId
            });
        localStorage.removeItem(namespaceTokenInLocalStorageKey);
    }
}

export class WebSocketsService {
    private readonly webSocketsAdapter: IWebSocketsAdapter;
    private readonly websocketTokensStore: WebsocketTokensStore;
    private readonly loggerService: LoggerService;

    authenticatedResolve: () => void;
    waitForAuthenticated: Promise<void> = new Promise(resolve => {
        this.authenticatedResolve = resolve;
    });

    @observable isAuthenticated: boolean = false;
    joinedRooms: Record<string, Record<string, Function>> = {}; // <roomName, <callbackId, callback>> The reason is that hooks can register multiple times to the same room name.
    reconnectCallbacks: Record<string, Function> = {};

    constructor({
        webSocketsAdapter
    }: {
        webSocketsAdapter: IWebSocketsAdapter;
    }) {
        this.webSocketsAdapter = webSocketsAdapter;
        this.websocketTokensStore = new WebsocketTokensStore(webSocketsAdapter);
        this.loggerService = new LoggerService({
            featureName: 'websocketV3',
            messagePrefix: '[WEBSOCKET_V3]'
        });

        this._onRoomMessage.bind(this);
        this.initWaitForAuthentication();
    }

    initWaitForAuthentication() {
        this.waitForAuthenticated = new Promise(resolve => {
            this.authenticatedResolve = resolve;
        });
    }

    @action.bound
    setIsAuthenticated(isAuthenticated) {
        this.isAuthenticated = isAuthenticated;
        if (isAuthenticated) {
            this.authenticatedResolve();
        } else {
            this.initWaitForAuthentication();
        }
    }

    connect({ auth }: { auth: SocketAuth }) {
        const success = this.webSocketsAdapter.connect({ auth });
        if (!success) {
            this.loggerService.logError('failed to connect to socket');
            return;
        }

        this.webSocketsAdapter.socket.once('connect', () => {
            this.setIsAuthenticated(true);
            // this._authenticate(user, fingerprint)
        });

        this.webSocketsAdapter.socket.on('connect_error', () => {
            this.setIsAuthenticated(false);
        });

        this.webSocketsAdapter.socket.on('disconnect', () => {
            this.setIsAuthenticated(false);
        });

        this.webSocketsAdapter.socket.on('reconnect', () => {
            this.setIsAuthenticated(true);
            return this._onReconnect();
        });
    }

    disconnect() {
        if (!this.isConnected) {
            return;
        }

        const joinedRoomsIds = Object.keys(this.joinedRooms);
        if (joinedRoomsIds.length) {
            joinedRoomsIds.forEach(topic => {
                this.joinedRooms[topic] = {};
                this.leaveRoom({ topic, callbackId: '' });
            });
        }

        this.setIsAuthenticated(false);
        this.webSocketsAdapter.disconnect();
    }

    get isConnected() {
        return this.webSocketsAdapter.isConnected;
    }

    get isAuthorized() {
        return this.isConnected && this.isAuthenticated;
    }

    joinRoom({
        roomName,
        callback,
        callbackId,
        namespace,
        namespaceToken
    }: {
        roomName: string;
        callbackId: string;
        callback: Function;
        namespace: WebsocketNamespace;
        namespaceToken: string;
    }) {
        const topic = buildTopic({ namespace, roomName });

        if (this.joinedRooms[topic]) {
            this.joinedRooms[topic][callbackId] = callback;
            return;
        }

        this.joinedRooms[topic] = {
            [callbackId]: callback
        };
        this.webSocketsAdapter.socket.on(topic, message => {
            this._onRoomMessage(topic, message);
        });
        if (this.isAuthorized) {
            setTimeout(
                () =>
                    this.webSocketsAdapter.socket.emit('join', {
                        room: roomName,
                        token: namespaceToken
                    }),
                200
            );
        }

        return () =>
            this.leaveRoom({
                topic,
                callbackId
            });
    }

    leaveRoom({ topic, callbackId }: { topic: string; callbackId: string }) {
        if (!this.joinedRooms[topic]) {
            return;
        }

        delete this.joinedRooms[topic][callbackId];

        if (Object.values(this.joinedRooms[topic]).length) {
            return;
        }

        this.webSocketsAdapter.socket.off(topic);
        delete this.joinedRooms[topic];

        if (!this.isAuthorized) {
            return;
        }

        this.webSocketsAdapter.socket.emit('leave', { topic });
    }

    registerToReconnect({
        callbackId,
        onReconnect
    }: {
        callbackId: string;
        onReconnect: Function;
    }) {
        this.reconnectCallbacks[callbackId] = onReconnect;

        return () => this.unregisterFromReconnect(callbackId);
    }

    unregisterFromReconnect(callbackId: string) {
        delete this.reconnectCallbacks[callbackId];
    }

    isConnectedToRoom({
        roomName,
        namespace
    }: {
        roomName: string;
        namespace: WebsocketNamespace;
    }) {
        const topic = buildTopic({ namespace, roomName });

        return !!this.joinedRooms[topic];
    }

    emit({ name, data }: { name: string; data?: any }) {
        this.webSocketsAdapter.socket.emit(name, data);
    }

    async getNamespaceToken(namespace: WebsocketNamespace, intent?: string) {
        try {
            const token = await this.websocketTokensStore.getToken(
                namespace,
                intent
            );
            const { exp } = jwtDecode(token);
            const oneHourBuffer = 1000 * 60 * 60;
            const expiredDate = new Date(exp * 1000 + oneHourBuffer).getTime();
            const isExpired = Date.now() > expiredDate;
            if (isExpired) {
                this.loggerService.logWarn('token expired', {
                    namespace
                });

                throw {
                    name: 'TokenExpiredError',
                    message: `Token is expired; namespace: ${namespace}, expired at ${expiredDate}.`
                };
            }

            return token;
        } catch (error) {
            this.loggerService.trackError(error);

            // expected failures
            if (
                error.name === 'TokenExpiredError' ||
                error.name === InvalidTokenError.name
            ) {
                return this.grabFreshToken(namespace);
            }

            this.loggerService.logError('error getting namespace token', {
                namespace
            });
            throw error;
        }
    }

    private grabFreshToken(namespace: WebsocketNamespace) {
        this.websocketTokensStore.cleanNamespaceToken(namespace);

        return this.websocketTokensStore.getToken(namespace);
    }

    private _onRoomMessage(topic: string, message: any) {
        const callbacks = Object.values(this.joinedRooms[topic]);
        const jsonData = transformJsonData(message);

        callbacks.forEach(callback => {
            callback(jsonData);
        });
    }

    private _onReconnect() {
        if (!this.isAuthorized) {
            return;
        }

        Object.values(this.reconnectCallbacks).forEach(callback => {
            callback();
        });
    }
}
